/**
 * Identidade visual do domo do Will.
 *
 * Isto é referência preservada da interface anterior, não invenção: as 14
 * expressões, suas cores e seus textos vieram do componente publicado em
 * willone.com.br. O domo é a única coisa que atravessa a reescrita intacta —
 * é ele que faz o Will ser reconhecível.
 *
 * Estado afetivo (aqui) e estado conversacional (conversation.ts) são coisas
 * diferentes e não devem ser fundidos: o conversacional descreve o que o
 * runtime está fazendo; o afetivo descreve como o Will se apresenta enquanto
 * faz. O mesmo THINKING pode aparecer como 'thinking' ou 'focus'.
 */

export const DOME_STATES = [
  'listening', 'thinking', 'opportunity', 'focus', 'busy', 'worried',
  'completed', 'speaking', 'awaiting', 'unclear', 'failed', 'waking',
  'sad', 'resting',
] as const;

export type DomeState = (typeof DOME_STATES)[number];

/** A paleta do domo: sete cores fazendo catorze expressões. */
export const DOME_PALETTE = {
  signal:  '#36c7ff', // atenção neutra — ouvindo, pensando, falando, acordando
  growth:  '#32e3a1', // oportunidade
  deep:    '#8f5bff', // foco e incompreensão
  effort:  '#ff8c2a', // trabalho em curso
  alert:   '#ff6767', // preocupação e falha
  calm:    '#5fe8d6', // conclusão e descanso
  pending: '#ffb347', // aguardando a pessoa
  muted:   '#8fc7e8', // tristeza
} as const;

export type DomeColor = (typeof DOME_PALETTE)[keyof typeof DOME_PALETTE];

export interface DomeExpression {
  /** chave estável — usada em eventos e telemetria */
  key: DomeState;
  /** rótulo em português para a interface */
  label: string;
  color: DomeColor;
  /** título curto exibido junto ao domo */
  greeting: string;
  /** legenda que explica o estado */
  sub: string;
}

export const DOME: Record<DomeState, DomeExpression> = {
  listening: {
    key: 'listening', label: 'Ouvindo', color: DOME_PALETTE.signal,
    greeting: 'Pode falar', sub: 'Atento e receptivo.',
  },
  thinking: {
    key: 'thinking', label: 'Pensando', color: DOME_PALETTE.signal,
    greeting: 'Deixa eu pensar…', sub: 'Cruzando o que você pediu com o que já sei.',
  },
  opportunity: {
    key: 'opportunity', label: 'Oportunidade', color: DOME_PALETTE.growth,
    greeting: 'Achei algo bom', sub: 'Otimista e atento a possibilidades.',
  },
  focus: {
    key: 'focus', label: 'Em foco', color: DOME_PALETTE.deep,
    greeting: 'Analisando', sub: 'Concentrado e analítico.',
  },
  busy: {
    key: 'busy', label: 'Atarefado', color: DOME_PALETTE.effort,
    greeting: 'Executando agora', sub: 'Processando o plano, passo a passo.',
  },
  worried: {
    key: 'worried', label: 'Preocupado', color: DOME_PALETTE.alert,
    greeting: 'Preciso de você', sub: 'Cauteloso e atento aos detalhes.',
  },
  completed: {
    key: 'completed', label: 'Concluído', color: DOME_PALETTE.calm,
    greeting: 'Tudo certo', sub: 'Objetivo alcançado.',
  },
  speaking: {
    key: 'speaking', label: 'Falando', color: DOME_PALETTE.signal,
    greeting: 'Respondendo', sub: 'Fala em andamento.',
  },
  awaiting: {
    key: 'awaiting', label: 'Aguardando você', color: DOME_PALETTE.pending,
    greeting: 'Confirma?', sub: 'Preciso da sua autorização para seguir.',
  },
  unclear: {
    key: 'unclear', label: 'Não entendi', color: DOME_PALETTE.deep,
    greeting: 'Não peguei essa', sub: 'Ouvi só parte do que você disse.',
  },
  failed: {
    key: 'failed', label: 'Falhou', color: DOME_PALETTE.alert,
    greeting: 'Não consegui', sub: 'A ação não foi concluída.',
  },
  waking: {
    key: 'waking', label: 'Acordando', color: DOME_PALETTE.signal,
    greeting: 'Voltando', sub: 'Retomando a sessão.',
  },
  sad: {
    key: 'sad', label: 'Triste', color: DOME_PALETTE.muted,
    greeting: 'Sinto muito', sub: 'Reconhecendo uma notícia ruim.',
  },
  resting: {
    key: 'resting', label: 'Em descanso', color: DOME_PALETTE.calm,
    greeting: 'Tudo tranquilo', sub: 'Ocioso, mas acordo se precisar.',
  },
};

export const isDomeState = (v: unknown): v is DomeState =>
  typeof v === 'string' && (DOME_STATES as readonly string[]).includes(v);
